import { useRef } from "react";
import { useConfirmDialogFocus } from "@/components/shared/useConfirmDialogFocus";

type ConfirmModalProps = {
  title: string;
  message: React.ReactNode;
  cancelText?: string;
  confirmText?: string;
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Focus target to fall back to if confirming removes the element that
   * originally had focus (e.g. the "Remove" button that opened this modal).
   */
  fallbackFocusRef?: React.RefObject<HTMLElement | null>;
};

export function ConfirmModal({
  title,
  message,
  cancelText = "Continue Editing",
  confirmText = "Confirm",
  onCancel,
  onConfirm,
  fallbackFocusRef,
}: ConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const handleKeyDown = useConfirmDialogFocus(
    cancelRef,
    confirmRef,
    onCancel,
    fallbackFocusRef
  );

  return (
    <div className="wizard-overlay">
      <div
        className="wizard-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wizard-confirm-title"
        aria-describedby="wizard-confirm-text"
      >
        <h5 id="wizard-confirm-title" className="wizard-confirm-title">
          {title}
        </h5>
        <div id="wizard-confirm-text" className="wizard-confirm-text">
          {message}
        </div>
        <div className="wizard-confirm-actions">
          <button
            ref={cancelRef}
            onClick={onCancel}
            onKeyDown={handleKeyDown}
            className="wizard-btn-cancel"
          >
            {cancelText}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            onKeyDown={handleKeyDown}
            className="wizard-btn-confirm"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
